import { useContext, useEffect, useRef, useState } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { UNSAFE_NavigationContext } from 'react-router-dom';

import type { UploadSession } from './upload-status-indicator';
import { APIBaseChronos } from '@/api/hosts';

interface UseUploadGuardsParams {
  isUploading: boolean;
  uploadSessionsRef: React.MutableRefObject<UploadSession[]>;
  matterId: string;
}

export function useUploadGuards({ isUploading, uploadSessionsRef, matterId }: UseUploadGuardsParams) {
  const authInfo = useAuthInfo();
  const accessTokenRef = useRef<string | null>(null);
  accessTokenRef.current = authInfo.accessToken ?? null;

  // Warn on tab close; signal done for all sessions if the user actually leaves
  useEffect(() => {
    if (!isUploading) return;

    const warnHandler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    const unloadHandler = () => {
      const token = accessTokenRef.current;
      for (const session of uploadSessionsRef.current) {
        fetch(`${APIBaseChronos}/api/matter/${matterId}/upload-metadata/${session.uploadId}/done`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          keepalive: true,
        });
      }
    };

    window.addEventListener('beforeunload', warnHandler);
    window.addEventListener('pagehide', unloadHandler);
    return () => {
      window.removeEventListener('beforeunload', warnHandler);
      window.removeEventListener('pagehide', unloadHandler);
    };
  }, [isUploading, matterId]);

  // Block React Router navigation while uploading — shows confirmation modal
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const [pendingNavigation, setPendingNavigation] = useState<{ action: 'push' | 'replace'; args: unknown[] } | null>(
    null,
  );
  const originalNavigatorRef = useRef<{ push: typeof navigator.push; replace: typeof navigator.replace } | null>(null);

  useEffect(() => {
    if (!isUploading) return;

    originalNavigatorRef.current = {
      push: navigator.push.bind(navigator),
      replace: navigator.replace.bind(navigator),
    };

    navigator.push = (...args: unknown[]) => {
      setPendingNavigation({ action: 'push', args });
    };
    navigator.replace = (...args: unknown[]) => {
      setPendingNavigation({ action: 'replace', args });
    };

    return () => {
      if (originalNavigatorRef.current) {
        navigator.push = originalNavigatorRef.current.push;
        navigator.replace = originalNavigatorRef.current.replace;
      }
    };
  }, [isUploading, navigator]);

  return {
    pendingNavigation,
    setPendingNavigation,
    originalNavigatorRef,
  };
}
